import '@pixi/layout';
import 'pixi.js/accessibility';
import { Application } from 'pixi.js';
import type { GameState, GameEvent, BoardPosition } from '@/types';
import { getStore } from '@state/store';
import { WarriorLoader } from '@services/WarriorLoader';
import { SoundManager } from '@services/SoundManager';
import { MusicManager } from '@services/MusicManager';
import { AIController } from '@/ai';
import {
  createNetworkController,
  clearNetworkController,
  type NetworkController,
  type NetworkRole,
} from '@network/NetworkController';
import { ScreenManager, SceneKey } from './ScreenManager';
import { LayoutManager } from './LayoutManager';
import { COLORS } from './constants/theme';
import { TitleScene } from './scenes/TitleScene';
import { WarriorSelectionScene } from './scenes/WarriorSelectionScene';
import { AIDifficultyScene } from './scenes/AIDifficultyScene';
import { RoomScene } from './scenes/RoomScene';
import { CoinFlipScene } from './scenes/CoinFlipScene';
import { GameBoardScene } from './scenes/GameBoardScene';
import { GameOverScene } from './scenes/GameOverScene';
import { PixiEffects } from './effects/PixiEffects';
import { BoardContainer } from './components/BoardContainer';
import { InviteGate } from './components/InviteGate';

/**
 * Main PixiJS application class
 * Replaces the DOM-based App.ts
 */
export class PixiApp {
  private app: Application;
  private screenManager: ScreenManager;
  private layoutManager: LayoutManager;
  private effects: PixiEffects;
  private unsubscribe: (() => void) | null = null;
  private unsubscribeEvents: (() => void) | null = null;
  private aiController: AIController | null = null;
  private networkController: NetworkController | null = null;
  private networkRole: NetworkRole | null = null;
  private gameBoardScene: GameBoardScene | null = null;
  private warriorSelectionScene: WarriorSelectionScene | null = null;
  private inviteGate: InviteGate | null = null;
  private onInviteVerified: (() => void) | null = null;
  private currentPlayerSelection: 1 | 2 = 1;
  private lastPlayerSelection: 1 | 2 = 1;

  // Player 1 data (passed between scenes)
  private player1Data: { name: string; selections: string[]; rating: number } = {
    name: 'Player 1',
    selections: [],
    rating: 1000,
  };

  // Player 2 data (passed between scenes)
  private player2Data: { name: string; selections: string[]; rating: number } = {
    name: 'Player 2',
    selections: [],
    rating: 1000,
  };

  // Flags for online team synchronization
  private localTeamSent: boolean = false;
  private remoteTeamReceived: boolean = false;

  // Store coin flip result for synchronization (host sends, guest receives)
  private coinFlipResult: 'player1' | 'player2' | null = null;

  // Store last attacker/healer position for lightning effects
  private lastActionPixelPos: { x: number; y: number } | null = null;

  constructor() {
    this.app = new Application();
    this.screenManager = new ScreenManager();
    this.layoutManager = new LayoutManager();
    this.effects = new PixiEffects();
  }

  /**
   * Initialize the PixiJS application
   */
  async init(container: HTMLElement): Promise<void> {
    // Initialize PixiJS
    await this.app.init({
      resizeTo: window,
      backgroundColor: COLORS.BG_PRIMARY,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    container.appendChild(this.app.canvas);

    // Set up container hierarchy
    this.app.stage.addChild(this.screenManager.container);
    this.app.stage.addChild(this.effects.container);

    // Handle resize
    window.addEventListener('resize', this.handleResize.bind(this));
    this.handleResize();

    // Load warriors and set up game
    const store = getStore();
    const engine = store.getEngine();

    try {
      const warriors = await WarriorLoader.loadWarriors();
      engine.loadWarriors(warriors);
    } catch (error) {
      console.error('Failed to load warriors:', error);
      return;
    }

    // Create and register scenes
    this.createScenes();

    // Subscribe to game events
    this.unsubscribeEvents = engine.subscribe({
      onGameEvent: (event: GameEvent) => this.handleGameEvent(event),
    });

    // Subscribe to state changes
    this.unsubscribe = store.subscribe((state) => this.onStateChange(state));

    // Start the game loop
    this.app.ticker.add((ticker) => {
      this.screenManager.update(ticker.deltaTime);
      this.effects.update(ticker.deltaTime);
    });
  }

  /**
   * Initialize with invite gate first
   */
  async initWithInviteGate(container: HTMLElement, onVerified: () => void): Promise<void> {
    this.onInviteVerified = onVerified;

    // Initialize PixiJS
    await this.app.init({
      resizeTo: window,
      backgroundColor: COLORS.BG_PRIMARY,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    container.appendChild(this.app.canvas);

    // Handle resize
    window.addEventListener('resize', this.handleInviteGateResize.bind(this));

    // Create and show invite gate
    this.inviteGate = new InviteGate({
      width: window.innerWidth,
      height: window.innerHeight,
      onVerified: () => this.onInviteCodeVerified(),
    });
    this.app.stage.addChild(this.inviteGate.container);
  }

  /**
   * Handle resize during invite gate phase
   */
  private handleInviteGateResize(): void {
    if (this.inviteGate) {
      this.inviteGate.resize(window.innerWidth, window.innerHeight);
    }
  }

  /**
   * Called when invite code is verified
   */
  private async onInviteCodeVerified(): Promise<void> {
    // Clean up invite gate
    if (this.inviteGate) {
      this.inviteGate.destroy();
      this.app.stage.removeChild(this.inviteGate.container);
      this.inviteGate = null;
    }

    // Remove invite gate resize handler
    window.removeEventListener('resize', this.handleInviteGateResize.bind(this));

    // Call the verified callback (starts music)
    this.onInviteVerified?.();

    // Continue with normal initialization
    // Set up container hierarchy
    this.app.stage.addChild(this.screenManager.container);
    this.app.stage.addChild(this.effects.container);

    // Handle resize
    window.addEventListener('resize', this.handleResize.bind(this));
    this.handleResize();

    // Load warriors and set up game
    const store = getStore();
    const engine = store.getEngine();

    try {
      const warriors = await WarriorLoader.loadWarriors();
      engine.loadWarriors(warriors);
    } catch (error) {
      console.error('Failed to load warriors:', error);
      return;
    }

    // Create and register scenes
    this.createScenes();

    // Subscribe to game events
    this.unsubscribeEvents = engine.subscribe({
      onGameEvent: (event: GameEvent) => this.handleGameEvent(event),
    });

    // Subscribe to state changes
    this.unsubscribe = store.subscribe((state) => this.onStateChange(state));

    // Start the game loop (if not already started)
    if (this.app.ticker.count === 0) {
      this.app.ticker.add((ticker) => {
        this.screenManager.update(ticker.deltaTime);
        this.effects.update(ticker.deltaTime);
      });
    }
  }

  private createScenes(): void {
    const store = getStore();
    const engine = store.getEngine();
    const metrics = this.layoutManager.getMetrics();

    // Title Scene - 3 mode buttons
    const titleScene = new TitleScene(metrics, {
      on1Player: () => engine.startGame('1-player'),
      on2Player: () => engine.startGame('2-player'),
      on2PlayerOnline: () => engine.startGame('online'),
    });
    this.screenManager.registerScene('title', titleScene);

    // Warrior Selection Scene (unified for P1 and P2)
    this.warriorSelectionScene = new WarriorSelectionScene(metrics, {
      getState: () => store.getState(),
      getPlayerNumber: () => {
        // In online mode, player number is based on network role
        if (this.networkRole) {
          return this.networkRole === 'host' ? 1 : 2;
        }
        return this.currentPlayerSelection;
      },
      getPlayerColor: () => {
        if (this.networkRole) {
          return this.networkRole === 'host' ? COLORS.PLAYER1 : COLORS.PLAYER2;
        }
        return this.currentPlayerSelection === 1 ? COLORS.PLAYER1 : COLORS.PLAYER2;
      },
      getDefaultName: () => {
        if (this.networkRole) {
          return this.networkRole === 'host' ? 'Player 1' : 'Player 2';
        }
        return this.currentPlayerSelection === 1 ? 'Player 1' : 'Player 2';
      },
      getDefaultRating: () => 1000,
      onNext: (name, selections, rating) => {
        const state = store.getState();

        if (state.gameMode === 'online') {
          // Online mode - store based on network role
          if (this.networkRole === 'host') {
            this.player1Data = { name, selections, rating };
          } else {
            this.player2Data = { name, selections, rating };
          }
          // Send team and wait for opponent
          this.sendLocalTeamOnline();
        } else if (this.currentPlayerSelection === 1) {
          // Store player 1 data
          this.player1Data = { name, selections, rating };

          // Next step depends on game mode
          if (state.gameMode === '1-player') {
            // Go to AI difficulty selection
            engine.proceedToAIDifficulty();
          } else if (state.gameMode === '2-player') {
            // Go to player 2 warrior selection
            this.currentPlayerSelection = 2;
            engine.proceedToWarriorSelection();
          }
        } else {
          // Store player 2 data (local 2-player mode)
          this.player2Data = { name, selections, rating };

          // Confirm team selection and proceed to coin flip
          engine.confirmTeamSelection(
            this.player1Data.name, this.player1Data.selections, this.player1Data.rating,
            name, selections, rating,
            null // No AI in 2-player mode
          );
          engine.proceedToCoinFlip();
        }
      },
      onBack: () => {
        const state = store.getState();

        if (this.currentPlayerSelection === 2) {
          // Go back to player 1 selection
          this.currentPlayerSelection = 1;
          engine.proceedToWarriorSelection();
        } else if (state.gameMode === 'online') {
          // Go back to room scene
          engine.proceedToRoom();
        } else {
          // Go back to title
          engine.transitionTo('title');
        }
      },
    });
    this.screenManager.registerScene('warrior-selection', this.warriorSelectionScene);

    // AI Difficulty Scene
    const aiDifficultyScene = new AIDifficultyScene(metrics, {
      onSelectDifficulty: (difficulty) => {
        // Mirror player 1's warriors for AI
        this.player2Data = {
          name: 'AI Warrior',
          selections: [...this.player1Data.selections],
          rating: difficulty === 'easy' ? 800 : difficulty === 'medium' ? 1000 : 1200,
        };

        // Set game mode with AI config
        engine.setGameMode('1-player', difficulty);

        // Create AI controller
        this.aiController = new AIController(engine, difficulty, {
          onThinkingStart: () => this.gameBoardScene?.showAIThinking(),
          onThinkingEnd: () => this.gameBoardScene?.hideAIThinking(),
          onThinkingProgress: (progress) => this.gameBoardScene?.updateAIThinkingProgress(progress),
        });

        // Confirm team selection
        engine.confirmTeamSelection(
          this.player1Data.name, this.player1Data.selections, this.player1Data.rating,
          this.player2Data.name, this.player2Data.selections, this.player2Data.rating,
          difficulty
        );

        // Proceed to coin flip
        engine.proceedToCoinFlip();
      },
      onBack: () => {
        // Go back to warrior selection (player 1)
        this.currentPlayerSelection = 1;
        engine.proceedToWarriorSelection();
      },
    });
    this.screenManager.registerScene('ai-difficulty', aiDifficultyScene);

    // Room Scene (online multiplayer)
    const roomScene = new RoomScene(metrics, {
      onConnected: (isHost) => this.handleOnlineConnected(isHost),
      onBack: () => {
        engine.transitionTo('title');
      },
    });
    this.screenManager.registerScene('room', roomScene);

    // Coin Flip Scene
    const coinFlipScene = new CoinFlipScene(metrics, {
      getPlayer1Name: () => this.player1Data.name,
      getPlayer2Name: () => this.player2Data.name,
      onCoinFlip: () => {
        // For online mode, use the pre-synchronized result
        if (store.getState().gameMode === 'online' && this.coinFlipResult) {
          console.log('[PixiApp] Using synchronized coin flip result:', this.coinFlipResult);
          return this.coinFlipResult;
        }
        // For offline modes, perform coin flip normally
        return engine.performCoinFlip();
      },
      onComplete: () => {
        MusicManager.stop();
        engine.startBattle();
      },
    });
    this.screenManager.registerScene('coin-flip', coinFlipScene);

    // Game Board Scene
    this.gameBoardScene = new GameBoardScene(metrics, {
      getState: () => store.getState(),
      getNetworkRole: () => this.networkRole,
      getNetworkController: () => this.networkController,
      onWarriorSelect: (id) => {
        engine.selectWarrior(id);
        // Send to peer if online
        if (this.networkController && this.networkController.isLocalTurn()) {
          this.networkController.sendSelectWarrior(id);
        }
      },
      onMove: (pos) => {
        try {
          // Send to peer if online
          if (this.networkController && this.networkController.isLocalTurn()) {
            this.networkController.sendMove(pos);
          }
          engine.move(pos);
        } catch (error) {
          console.warn('Invalid move:', error);
        }
      },
      onDash: (pos) => {
        try {
          // Send to peer if online
          if (this.networkController && this.networkController.isLocalTurn()) {
            this.networkController.sendDash(pos);
          }
          engine.dash(pos);
        } catch (error) {
          console.warn('Invalid dash:', error);
        }
      },
      onAttack: () => {
        try {
          // Send to peer if online
          if (this.networkController && this.networkController.isLocalTurn()) {
            this.networkController.sendAttack();
          }
          engine.attack();
        } catch (error) {
          console.warn('Invalid attack:', error);
        }
      },
      onHeal: () => {
        try {
          // Send to peer if online
          if (this.networkController && this.networkController.isLocalTurn()) {
            this.networkController.sendHeal();
          }
          engine.heal();
        } catch (error) {
          console.warn('Invalid heal:', error);
        }
      },
    });
    this.screenManager.registerScene('battle', this.gameBoardScene);

    // Game Over Scene
    const gameOverScene = new GameOverScene(metrics, {
      getState: () => store.getState(),
      onPlayAgain: () => {
        engine.playAgain();
        engine.proceedToCoinFlip();
      },
      onStartOver: () => engine.reset(),
    });
    this.screenManager.registerScene('game-over', gameOverScene);

    // Subscribe layout manager to resize
    this.layoutManager.subscribe((newMetrics) => {
      this.screenManager.onResize(newMetrics);
    });
  }

  private handleResize(): void {
    this.layoutManager.onResize(window.innerWidth, window.innerHeight);
  }

  /**
   * Handle online connection established
   */
  private handleOnlineConnected(isHost: boolean): void {
    const store = getStore();
    const engine = store.getEngine();

    this.networkRole = isHost ? 'host' : 'guest';
    console.log('[PixiApp] Online connected, role:', this.networkRole);

    // Reset online team synchronization flags
    this.localTeamSent = false;
    this.remoteTeamReceived = false;
    this.coinFlipResult = null;

    // Create network controller with callbacks
    this.networkController = createNetworkController(engine, this.networkRole, {
      onConnected: () => {
        console.log('[PixiApp] Network connected');
      },
      onDisconnected: () => {
        console.log('[PixiApp] Network disconnected');
        this.handleNetworkDisconnect();
      },
      onError: (error) => {
        console.error('[PixiApp] Network error:', error);
      },
      onRemoteTeamReady: (playerName, warriorIds, rating) => {
        console.log('[PixiApp] Remote team ready:', playerName, warriorIds);
        this.remoteTeamReceived = true;

        // Store remote player's data based on their role
        // Host receives from guest (player2), guest receives from host (player1)
        if (this.networkRole === 'host') {
          this.player2Data = { name: playerName, selections: warriorIds, rating };
        } else {
          this.player1Data = { name: playerName, selections: warriorIds, rating };
        }

        // Check if both teams are ready
        this.checkAndProceedOnline();
      },
      onCoinFlipResult: (startingPlayer) => {
        console.log('[PixiApp] Coin flip result received:', startingPlayer);
        // Store result for coin flip animation
        this.coinFlipResult = startingPlayer;
        // Set starting player in engine
        engine.setStartingPlayer(startingPlayer);

        // If guest is waiting for result, now proceed to coin flip scene
        if (this.networkRole === 'guest' && this.localTeamSent && this.remoteTeamReceived) {
          console.log('[PixiApp] Guest received coin flip result, proceeding to coin flip');
          engine.proceedToCoinFlip();
        }
      },
      onRemoteWarriorSelected: (warriorInstanceId) => {
        console.log('[PixiApp] Remote warrior selected:', warriorInstanceId);
        engine.selectWarrior(warriorInstanceId);
      },
      onRemoteMove: (to) => {
        console.log('[PixiApp] Remote move:', to);
        engine.move(to);
      },
      onRemoteDash: (to) => {
        console.log('[PixiApp] Remote dash:', to);
        engine.dash(to);
      },
      onRemoteAttack: () => {
        console.log('[PixiApp] Remote attack');
        engine.attack();
      },
      onRemoteHeal: () => {
        console.log('[PixiApp] Remote heal');
        engine.heal();
      },
      onRematchRequest: () => {
        console.log('[PixiApp] Remote rematch request');
        // Handle rematch in GameOverScene
      },
    });

    // Set game mode to online (already set in startGame, but ensure it's set)
    engine.setGameMode('online');

    // Proceed to warrior selection
    this.currentPlayerSelection = 1;
    console.log('[PixiApp] Proceeding to warrior selection');
    engine.proceedToWarriorSelection();
  }

  /**
   * Handle network disconnection
   */
  private handleNetworkDisconnect(): void {
    const store = getStore();
    const engine = store.getEngine();

    // Clear network state
    this.networkController = null;
    this.networkRole = null;
    clearNetworkController();

    // If mid-game, return to title
    const state = store.getState();
    if (state.phase === 'battle' || state.phase === 'warrior-selection' || state.phase === 'room' || state.phase === 'coin-flip') {
      // Show disconnect message and reset
      console.log('[PixiApp] Disconnected mid-game, returning to title');
      engine.reset();
    }
  }

  private handleGameEvent(event: GameEvent): void {
    switch (event.type) {
      case 'warrior-selected':
        SoundManager.play('select');
        break;
      case 'warrior-moved':
        SoundManager.play('move');
        break;
      case 'warrior-dashed':
        SoundManager.play('dash');
        break;
      case 'warrior-attacked': {
        SoundManager.play('attack');
        const attackPayload = event.payload as { warrior: string; position: BoardPosition };
        // Store attacker position for lightning effects
        if (attackPayload.position) {
          this.lastActionPixelPos = this.layoutManager.boardToPixel(
            attackPayload.position.row,
            attackPayload.position.col
          );
        }
        // Play attack burst on the attacking warrior
        const battleSceneForAttack = this.screenManager.getScene<GameBoardScene>('battle');
        const boardContainerForAttack = battleSceneForAttack?.['boardContainer'] as BoardContainer | undefined;
        const attackerSprite = boardContainerForAttack?.getWarriorSprite(attackPayload.warrior);
        attackerSprite?.playAttackBurst();
        break;
      }
      case 'warrior-healed': {
        SoundManager.play('heal');
        const healPayload = event.payload as { warrior: string; healedWarriors: Record<string, number> };
        // Play heart effect on all healed warriors with lightning
        const battleSceneForHeal = this.screenManager.getScene<GameBoardScene>('battle');
        const boardContainerForHeal = battleSceneForHeal?.['boardContainer'] as BoardContainer | undefined;
        if (healPayload.healedWarriors && boardContainerForHeal) {
          // Get healer position
          const healerSprite = boardContainerForHeal.getWarriorSprite(healPayload.warrior);
          const healerWarrior = healerSprite?.getWarrior();
          let healerPixelPos: { x: number; y: number } | null = null;
          if (healerWarrior?.position) {
            healerPixelPos = this.layoutManager.boardToPixel(
              healerWarrior.position.row,
              healerWarrior.position.col
            );
          }

          for (const warriorId of Object.keys(healPayload.healedWarriors)) {
            const healedSprite = boardContainerForHeal.getWarriorSprite(warriorId);
            healedSprite?.playHeartEffect();

            // Draw lightning from healer to each healed warrior (except self)
            if (healerPixelPos && warriorId !== healPayload.warrior) {
              const healedWarrior = healedSprite?.getWarrior();
              if (healedWarrior?.position) {
                const targetPos = this.layoutManager.boardToPixel(
                  healedWarrior.position.row,
                  healedWarrior.position.col
                );
                this.effects.playHealLightning(
                  healerPixelPos.x,
                  healerPixelPos.y,
                  targetPos.x,
                  targetPos.y
                );
              }
            }
          }
        }
        break;
      }
      case 'warrior-damaged': {
        const damagePayload = event.payload as { warrior: string; damage: number; position: BoardPosition };
        // Play damage effect on the warrior sprite
        const battleScene = this.screenManager.getScene<GameBoardScene>('battle');
        const boardContainer = battleScene?.['boardContainer'] as BoardContainer | undefined;
        const warriorSprite = boardContainer?.getWarriorSprite(damagePayload.warrior);
        warriorSprite?.playDamageEffect();

        // Draw lightning from attacker to damaged warrior
        if (damagePayload.position && this.lastActionPixelPos) {
          const targetPos = this.layoutManager.boardToPixel(
            damagePayload.position.row,
            damagePayload.position.col
          );
          this.effects.playAttackLightning(
            this.lastActionPixelPos.x,
            this.lastActionPixelPos.y,
            targetPos.x,
            targetPos.y
          );
        }

        // Also show damage number
        if (damagePayload.position) {
          const pixelPos = this.layoutManager.boardToPixel(
            damagePayload.position.row,
            damagePayload.position.col
          );
          this.effects.playDamageNumber(pixelPos.x, pixelPos.y, damagePayload.damage);
        }
        break;
      }
      case 'warrior-defeated': {
        SoundManager.play('defeat');
        const payload = event.payload as { warrior: string; position?: BoardPosition };
        if (payload.position) {
          const pixelPos = this.layoutManager.boardToPixel(
            payload.position.row,
            payload.position.col
          );
          this.effects.playDefeatEffect(pixelPos.x, pixelPos.y);
        }
        break;
      }
      case 'game-ended':
        SoundManager.play('victory');
        break;
      case 'turn-changed':
        // Trigger AI turn if needed
        this.checkAndTriggerAITurn();
        break;
    }
  }

  /**
   * Check if AI should take a turn and trigger it
   */
  private checkAndTriggerAITurn(): void {
    const store = getStore();
    const engine = store.getEngine();

    if (engine.isAITurn() && this.aiController) {
      // Small delay to let the UI update first
      setTimeout(() => {
        this.aiController?.checkAndExecuteTurn();
      }, 100);
    }
  }

  private onStateChange(state: GameState): void {
    const sceneKey = this.mapPhaseToScene(state.phase);
    const previousScene = this.screenManager.getCurrentScene();
    console.log('[PixiApp] State change:', { phase: state.phase, sceneKey, previousScene });

    // Update the current scene's state if it's the battle scene
    if (state.phase === 'battle') {
      this.gameBoardScene?.updateState(state);
    }

    // Handle game reset - clear controllers
    if (state.phase === 'title') {
      // Reset player data
      this.player1Data = { name: 'Player 1', selections: [], rating: 1000 };
      this.player2Data = { name: 'Player 2', selections: [], rating: 1000 };
      this.currentPlayerSelection = 1;
      this.lastPlayerSelection = 1;

      if (state.gameMode === '2-player' || state.gameMode === 'online') {
        this.aiController = null;
      }
      // Clear network state on reset
      if (this.networkController) {
        this.networkController.disconnect();
        this.networkController = null;
        this.networkRole = null;
      }
    }

    // Transition to appropriate scene
    if (previousScene !== sceneKey) {
      this.screenManager.transitionTo(sceneKey);

      // If entering battle phase, check if AI goes first
      if (sceneKey === 'battle') {
        // Small delay to let scene initialize
        setTimeout(() => {
          this.checkAndTriggerAITurn();
        }, 500);
      }
    } else if (
      sceneKey === 'warrior-selection' &&
      this.currentPlayerSelection !== this.lastPlayerSelection
    ) {
      // Same scene but player selection changed (e.g., P1 → P2 in 2-player mode)
      // Refresh the scene to rebuild for the new player
      this.screenManager.refreshCurrentScene();
    }

    // Update last player selection tracking
    this.lastPlayerSelection = this.currentPlayerSelection;
  }

  private mapPhaseToScene(phase: GameState['phase']): SceneKey {
    const mapping: Record<GameState['phase'], SceneKey> = {
      title: 'title',
      'warrior-selection': 'warrior-selection',
      'ai-difficulty': 'ai-difficulty',
      'room': 'room',
      'coin-flip': 'coin-flip',
      battle: 'battle',
      'game-over': 'game-over',
    };
    return mapping[phase];
  }

  /**
   * Handle online mode after local player selects warriors
   * This sends the team data and waits for both teams to be ready
   */
  private sendLocalTeamOnline(): void {
    // Get local player data based on role
    const localData = this.networkRole === 'host' ? this.player1Data : this.player2Data;

    // Send team data to peer
    if (this.networkController) {
      this.networkController.sendTeamReady(
        localData.name,
        localData.selections,
        localData.rating
      );
    }

    this.localTeamSent = true;

    // Show waiting overlay if opponent hasn't sent their team yet
    if (!this.remoteTeamReceived && this.warriorSelectionScene) {
      this.warriorSelectionScene.showWaiting(() => {
        // On cancel, disconnect and go back to room
        this.handleNetworkDisconnect();
      });
    }

    // Check if both teams are ready
    this.checkAndProceedOnline();
  }

  /**
   * Check if both teams are ready and proceed to coin flip
   */
  private checkAndProceedOnline(): void {
    if (!this.localTeamSent || !this.remoteTeamReceived) {
      console.log('[PixiApp] Waiting for teams:', {
        localTeamSent: this.localTeamSent,
        remoteTeamReceived: this.remoteTeamReceived,
      });
      return;
    }

    console.log('[PixiApp] Both teams ready, proceeding to coin flip');

    // Hide waiting overlay
    this.warriorSelectionScene?.hideWaiting();

    const store = getStore();
    const engine = store.getEngine();

    // Both teams are ready, confirm selection
    engine.confirmTeamSelection(
      this.player1Data.name,
      this.player1Data.selections,
      this.player1Data.rating,
      this.player2Data.name,
      this.player2Data.selections,
      this.player2Data.rating,
      null // No AI in online mode
    );

    // HOST: Perform coin flip, send result, then proceed
    // GUEST: Wait for onCoinFlipResult callback before proceeding
    if (this.networkRole === 'host') {
      this.coinFlipResult = engine.performCoinFlip();
      console.log('[PixiApp] Host performed coin flip:', this.coinFlipResult);
      this.networkController?.sendCoinFlipResult(this.coinFlipResult);
      engine.proceedToCoinFlip();
    } else if (this.coinFlipResult) {
      // Guest already received coin flip result (rare race condition)
      console.log('[PixiApp] Guest already has coin flip result, proceeding');
      engine.proceedToCoinFlip();
    } else {
      // Guest waits for coin flip result from host
      // proceedToCoinFlip will be called in onCoinFlipResult callback
      console.log('[PixiApp] Guest waiting for coin flip result from host');
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.unsubscribe?.();
    this.unsubscribeEvents?.();
    window.removeEventListener('resize', this.handleResize.bind(this));
    this.screenManager.dispose();
    this.effects.dispose();
    this.app.destroy(true, { children: true });
  }
}
